import React from "react";

import { API } from "config";
import * as types from "types";
import type { ChatStreamParserState } from "../methods/parseChatStream";
import { parseChatStream } from "../methods/parseChatStream";
import { sseParser } from "../sseParser/sseParser";

const baseUrl = API.cortevaChain.url;

const prompt = `Create a title for the text above that captures its essence.
The title must be very short, 6 words or less.
Only provide the title itself.
If the text is insufficient to derive a significant title, respond with nothing at all — empty response.
Do not quote the title, no quotes.`;

const promptTemplate = (
  userMessage: types.Message,
  assistantMessage: types.Message
) =>
  `Text:
${userMessage.role}: ${userMessage.content}
${assistantMessage.role}: ${assistantMessage.content}

${prompt}

Title:
`;

const filePromptTemplate = (fileMessage: types.Message) =>
  `The text above is from a file named: "${fileMessage.content}"

${prompt}

Title:
`;

/**
 * Looks for either:
 * - a [user, assistant] message pair
 * - an uploaded file
 * in order to generate a title.
 */
const getTitleGenerationMessages = (
  messages: types.Message[]
): types.Message[] | undefined => {
  const userMessage = messages.find((message) => message.role === "user");
  const assistantMessage = messages.find(
    (message) => message.role === "assistant"
  );
  const fileMessage = messages.find((message) => message.role === "file");

  if (userMessage && assistantMessage) {
    return [
      {
        uuid: crypto.randomUUID(),
        role: "user",
        content: promptTemplate(userMessage, assistantMessage),
      },
    ];
  } else if (fileMessage) {
    return [
      fileMessage, // include the file contents above the title prompt
      {
        uuid: crypto.randomUUID(),
        role: "user",
        content: filePromptTemplate(fileMessage),
      },
    ];
  } else {
    // We don't have enough messages yet to generate a title.
    return undefined;
  }
};

interface useChatTitleStreamOptions {
  chatThreadId: string;
  personaId: types.Persona["id"];
  model: string;
  messages: types.Message[];
  enabled: boolean;
  title: string;
  setTitle: (newTitle: string) => void;
  getAccessToken: () => Promise<string>;
  /**
   * Called when the stream is ended.
   */
  onStreamEnd?: (finalTitle: string) => void;
}

export default function useAutomaticChatTitle({
  chatThreadId,
  personaId,
  model,
  messages,
  enabled,
  title,
  setTitle,
  getAccessToken,
  onStreamEnd,
}: useChatTitleStreamOptions) {
  const xhrRef = React.useRef<XMLHttpRequest | null>(null);
  const [isStreaming, setIsStreaming] = React.useState(false);
  // Chat threads created by file upload have a tendency to produce empty titles
  // which can trigger an infinite loop. To avoid this, we keep track of message
  // arrays that have already been attempted.
  const attemptedMessagesRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    // Do nothing if title generation is disabled.
    if (!enabled) {
      return;
    }

    // Only generate a title if this is a new chat.
    if (title !== types.DEFAULT_CHAT_THREAD_NAME) {
      return;
    }

    // Do not start a new request if one already exists.
    if (xhrRef.current !== null) {
      return;
    }

    const titleGenerationMessages = getTitleGenerationMessages(messages);
    // Do nothing if we don't have enough messages yet.
    if (!titleGenerationMessages) {
      return;
    }

    // Do nothing if we have already tried to generate a title using these
    // messages.
    const messagesKey = messages.map((m) => m.uuid).join(",");
    if (attemptedMessagesRef.current.has(messagesKey)) {
      return;
    }
    attemptedMessagesRef.current.add(messagesKey);

    xhrRef.current = new XMLHttpRequest();

    async function fetchChatCompletion() {
      const accessToken = await getAccessToken();

      const xhr = xhrRef.current;
      if (xhr === null) return;

      const url = `${baseUrl}/chat/completions`;
      xhr.open("POST", url);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);

      xhr.onprogress = (_event: ProgressEvent<EventTarget>) => {
        if (!titleGenerationMessages) return;

        // Assume we have a stream of data events
        // parse the stream
        const responseText = xhr.responseText;
        const sseEvents = sseParser(responseText);

        let parserState: ChatStreamParserState = {
          eventsProcessed: 0,
          messages: JSON.parse(JSON.stringify(titleGenerationMessages)),
          unassignedMcpEvents: [],
          unassignedTokenUsage: null,
          errorMessage: null,
        };
        parseChatStream(parserState, sseEvents);

        // Find by role, not index. Compaction events can shift positions.
        const titleResponse = parserState.messages.find(
          (msg) => msg.role === "assistant" && msg.content !== ""
        );
        if (!titleResponse) return;
        setTitle(titleResponse.content);
      };

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          setIsStreaming(false);
          // Clear on every completion path so a non-200 doesn't leave the retry
          // guard (xhrRef.current !== null) permanently blocked.
          xhrRef.current = null;
          if (xhr.status === 200) {
            if (onStreamEnd === undefined) return;
            if (!titleGenerationMessages) return;
            // Assume we have a stream of data events
            // parse the stream
            const responseText = xhr.responseText;
            const sseEvents = sseParser(responseText);

            let parserState: ChatStreamParserState = {
              eventsProcessed: 0,
              messages: JSON.parse(JSON.stringify(titleGenerationMessages)),
              unassignedMcpEvents: [],
              unassignedTokenUsage: null,
              errorMessage: null,
            };
            parseChatStream(parserState, sseEvents);

            const titleResponse = parserState.messages.find(
              (msg) => msg.role === "assistant" && msg.content !== ""
            );
            if (!titleResponse) return;
            onStreamEnd(titleResponse.content);
          } else {
            console.error("Request failed with status " + xhr.status);
          }
        }
      };

      xhr.onerror = (event) => {
        setIsStreaming(false);
        xhrRef.current = null;
        console.error("The request to get title encountered an error: ", event);
      };

      const requestOptionsString = JSON.stringify({
        chat_thread_id: chatThreadId,
        persona_id: String(personaId),
        model,
        messages: titleGenerationMessages,
      });

      setIsStreaming(true);
      xhr.send(requestOptionsString);
    }

    fetchChatCompletion();
  }, [
    chatThreadId,
    personaId,
    model,
    messages,
    enabled,
    title,
    setTitle,
    getAccessToken,
    onStreamEnd,
  ]);

  return isStreaming;
}
